import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  Wallet,
  ScrollText,
  Users2,
  ClipboardList,
  FileSignature,
  Settings,
  Landmark,
  LogOut,
  Menu,
  X,
  WifiOff,
  HandCoins,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import QuickPaymentDialog from "@/components/QuickPaymentDialog";

const ALL_NAV = [
  { to: "/dashboard", label: "Özet", icon: LayoutDashboard, perm: null },
  { to: "/projects", label: "Projeler", icon: Building2, perm: "projects.view" },
  { to: "/muhasebe", label: "Muhasebe", icon: Wallet, perm: "muhasebe.view" },
  { to: "/cekler", label: "Çekler", icon: ScrollText, perm: "cekler.view" },
  { to: "/cariler", label: "Cariler", icon: Users2, perm: "cariler.view" },
  { to: "/hakedis", label: "Hakediş", icon: ClipboardList, perm: "hakedis.view" },
  { to: "/sozlesmeler", label: "Sözleşmeler", icon: FileSignature, perm: "sozlesmeler.view" },
  { to: "/kurumsal", label: "Kurumsal", icon: Landmark, perm: "kurumsal.view" },
  { to: "/ayarlar", label: "Ayarlar", icon: Settings, perm: null },
];

export default function Layout() {
  const { user, hasPerm, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [quickPayOpen, setQuickPayOpen] = useState(false);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  const nav = ALL_NAV.filter((n) => !n.perm || hasPerm(n.perm));
  // 4 tabs + centered FAB (Hızlı Ödeme). Pick 4 most-used sections; rest available via drawer.
  const mobileNav = nav.filter((n) => ["/dashboard", "/projects", "/muhasebe", "/cariler"].includes(n.to));
  const canQuickPay = hasPerm("muhasebe.create");

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 border-r border-border bg-background flex-col z-30">
        <div className="h-16 flex items-center gap-3 px-6 border-b border-border">
          <div className="w-10 h-10 rounded-md overflow-hidden bg-white ring-1 ring-border flex items-center justify-center shrink-0">
            <img src="/logo.png" alt="MMF Grup" className="w-full h-full object-contain" />
          </div>
          <div>
            <div className="font-heading font-bold text-base tracking-tight leading-none">
              MMF Grup
            </div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mt-1">
              İnşaat Mimarlık
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.to.replace("/", "")}`}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-150",
                  isActive
                    ? "bg-brand text-brand-foreground"
                    : "text-foreground hover:bg-secondary"
                )
              }
            >
              <item.icon className="w-4 h-4" strokeWidth={2} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        {canQuickPay && (
          <div className="px-3 pb-3">
            <button
              data-testid="quick-pay-sidebar"
              onClick={() => setQuickPayOpen(true)}
              className="w-full flex items-center gap-2 justify-center py-3 rounded-md bg-brand text-brand-foreground font-semibold text-sm shadow-[0_6px_16px_-4px_hsl(var(--brand)/0.4)] hover:brightness-110 active:scale-[0.98] transition-all"
            >
              <HandCoins className="w-4 h-4" strokeWidth={2.25} />
              Hızlı Ödeme
            </button>
          </div>
        )}
        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-8 h-8 rounded-full bg-secondary border border-border flex items-center justify-center font-heading font-semibold text-sm">
              {user?.name?.[0]?.toUpperCase() || "K"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                {user?.role === "admin" ? "Yönetici" : "Şantiye Şefi"}
              </div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="p-2 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
              title="Çıkış Yap"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 lg:pl-64 pb-20 lg:pb-0 min-w-0">
        {/* Mobile header */}
        <header className="lg:hidden sticky top-0 z-20 h-14 border-b border-border bg-background/95 backdrop-blur px-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md overflow-hidden bg-white ring-1 ring-border flex items-center justify-center shrink-0">
              <img src="/logo.png" alt="MMF Grup" className="w-full h-full object-contain" />
            </div>
            <span className="font-heading font-bold text-base tracking-tight">MMF Grup</span>
          </div>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMobileOpen(true)}
            className="p-2 rounded-md hover:bg-secondary"
          >
            <Menu className="w-5 h-5" />
          </button>
        </header>

        {!online && (
          <div
            data-testid="offline-banner"
            className="bg-warning/15 text-warning border-b border-warning/30 px-4 py-2 text-xs font-medium flex items-center gap-2"
          >
            <WifiOff className="w-4 h-4" /> Çevrimdışı mod — değişiklikler senkronize edilecek
          </div>
        )}

        <div className="max-w-[1400px] mx-auto p-4 lg:p-8 animate-fade-in">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom tab bar with centered FAB */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 h-16 bg-background border-t border-border">
        <div className="grid grid-cols-5 h-full">
          {mobileNav.slice(0, 2).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`tab-${item.to.replace("/", "")}`}
              className={({ isActive }) =>
                cn(
                  "flex flex-col items-center justify-center gap-1 text-[10px] font-medium transition-colors",
                  isActive ? "text-brand" : "text-muted-foreground"
                )
              }
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
          {/* Center spacer for FAB */}
          <div aria-hidden="true" />
          {mobileNav.slice(2, 4).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`tab-${item.to.replace("/", "")}`}
              className={({ isActive }) =>
                cn(
                  "flex flex-col items-center justify-center gap-1 text-[10px] font-medium transition-colors",
                  isActive ? "text-brand" : "text-muted-foreground"
                )
              }
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </div>

        {/* Centered Quick Payment FAB — sits above the bar */}
        {canQuickPay && (
          <button
            data-testid="quick-pay-fab"
            onClick={() => setQuickPayOpen(true)}
            aria-label="Hızlı Ödeme"
            className="absolute left-1/2 -top-6 -translate-x-1/2 w-16 h-16 rounded-full bg-brand text-brand-foreground shadow-[0_8px_24px_-4px_hsl(var(--brand)/0.5)] flex flex-col items-center justify-center gap-0.5 ring-4 ring-background transition-transform hover:scale-105 active:scale-95"
          >
            <HandCoins className="w-6 h-6" strokeWidth={2.25} />
            <span className="text-[9px] uppercase tracking-wider font-bold leading-none">Ödeme</span>
          </button>
        )}
      </nav>

      {/* Mobile drawer for extra nav items */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        >
          <div
            className="absolute right-0 top-0 bottom-0 w-72 bg-background border-l border-border p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="font-heading font-bold">Menü</div>
              <button
                onClick={() => setMobileOpen(false)}
                className="p-2 rounded-md hover:bg-secondary"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-1">
              {nav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium",
                      isActive ? "bg-brand text-brand-foreground" : "hover:bg-secondary"
                    )
                  }
                >
                  <item.icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium hover:bg-secondary text-destructive"
              >
                <LogOut className="w-4 h-4" />
                <span>Çıkış Yap</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <QuickPaymentDialog open={quickPayOpen} onOpenChange={setQuickPayOpen} />
    </div>
  );
}
